import { Button } from "@/components/flexkit/Button";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import {
    Dialog,
    DialogHeader,
    DialogTitle,
    DialogContent,
    DialogBody,
    DialogFooter,
    DialogClose,
} from "@/components/podkit/dialog/Dialog";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { formatRepoUrlForDisplay } from "@/format/url";
import { TrackLocations } from "@/hooks/use-segment";
import { pluralize } from "@/utils/strings";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { SearchRepositoriesResponse_Repository } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useCallback, type FC } from "react";

export const RepoTriggerSampleDialog: FC<{
    repositories: PlainMessage<SearchRepositoriesResponse_Repository>[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    onLoadMore?: () => void;
}> = ({ repositories, open, onOpenChange, hasNextPage, isFetchingNextPage, onLoadMore }) => {
    const handleScrollEnd = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage && onLoadMore) {
            onLoadMore();
        }
    }, [hasNextPage, isFetchingNextPage, onLoadMore]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-track-location={TrackLocations.WorkflowEditPageRepoTriggerSampleDialog}>
                <DialogHeader>
                    <DialogTitle>
                        <span>
                            {repositories.length} {pluralize(repositories.length, "repository", "repositories")}
                        </span>
                        {hasNextPage && <span> (more available)</span>}
                    </DialogTitle>
                </DialogHeader>
                <DialogBody className="p-0">
                    <ScrollArea
                        orientation="vertical"
                        className="max-h-80"
                        onScrollEnd={handleScrollEnd}
                        scrollEndThreshold={100}
                    >
                        <ul className="flex flex-col gap-2">
                            {repositories.map((repository) => (
                                <li
                                    className="flex flex-col rounded-md border border-border-subtle px-3 py-2"
                                    key={repository.url}
                                >
                                    <ExternalLink className="text-content-brand hover:underline" href={repository.url}>
                                        {formatRepoUrlForDisplay(repository.url)}
                                    </ExternalLink>
                                    <Text className="text-content-secondary">{repository.name}</Text>
                                </li>
                            ))}
                            {isFetchingNextPage && (
                                <li className="flex justify-center py-2">
                                    <Text className="animate-pulse text-content-secondary">Loading more...</Text>
                                </li>
                            )}
                        </ul>
                    </ScrollArea>
                </DialogBody>
                <DialogFooter className="justify-end">
                    <DialogClose asChild>
                        <Button
                            variant="outline"
                            data-tracking-id="workflow-edit-page-repo-trigger-sample-dialog-close"
                        >
                            Close
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
